import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import SEOHelmet from './SEOHelmet';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const OurWorkContainer = styled.div`
  margin: 0;
  padding: 0;
  min-height: 100vh;
  width: 100vw;
  max-width: 100%;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  position: relative;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;

  @media (min-width: 600px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 3rem 0;
  margin-bottom: 4rem;

  h1 {
    font-size: 3.5rem;
    font-weight: 300;
    line-height: 1.2;
    margin-bottom: 2rem;
    color: var(--text-primary);
    text-shadow: 0 0 20px rgba(157, 78, 221, 0.3);
  }

  p {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--text-secondary);
    max-width: 800px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
    margin-bottom: 3rem;
    
    h1 {
      font-size: 2.5rem;
      padding: 0 1rem;
    }
    
    p {
      font-size: 1.125rem;
      padding: 0 1.5rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0;
    margin-bottom: 2rem;
    
    h1 {
      font-size: 2rem;
      padding: 0 0.5rem;
    }
    
    p {
      font-size: 1rem;
      padding: 0 1rem;
    }
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin: 4rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin: 3rem 0;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
    margin: 2rem 0;
  }
`;

const ProjectCard = styled.div`
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};

  &:hover {
    transform: translateY(-10px);
    border-color: var(--accent-primary);
    box-shadow: 0 0 30px rgba(157, 78, 221, 0.3);
  }

  .project-header {
    margin-bottom: 1.5rem;
    
    h3 {
      font-size: 1.75rem;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: var(--accent-primary);
    }
    
    .project-type {
      font-size: 0.95rem;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 500;
    }
  }

  .project-description {
    margin-bottom: 2rem;
    
    p {
      font-size: 1rem;
      line-height: 1.6;
      color: var(--text-secondary);
      margin-bottom: 1rem;
    }
  }

  .project-tech {
    margin-bottom: 2rem;
    
    h4 {
      font-size: 0.9rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 600;
    }
    
    .tech-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    
    .tech-tag {
      background: rgba(157, 78, 221, 0.1);
      border: 1px solid rgba(157, 78, 221, 0.3);
      color: var(--accent-primary);
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
    }
  }

  .project-outcomes {
    h4 {
      font-size: 0.9rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 600;
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    
    li {
      font-size: 0.95rem;
      color: var(--text-secondary);
      margin-bottom: 0.5rem;
      padding-left: 1.5rem;
      position: relative;
      
      &:before {
        content: '→';
        position: absolute;
        left: 0;
        color: var(--accent-primary);
        font-weight: bold;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    
    .project-header {
      h3 {
        font-size: 1.5rem;
      }
      
      .project-type {
        font-size: 0.9rem;
      }
    }
    
    .project-description p {
      font-size: 0.95rem;
    }
    
    .project-tech {
      .tech-tag {
        font-size: 0.8rem;
        padding: 0.2rem 0.6rem;
      }
    }
    
    .project-outcomes li {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    
    .project-header {
      h3 {
        font-size: 1.375rem;
      }
      
      .project-type {
        font-size: 0.85rem;
      }
    }
    
    .project-description p {
      font-size: 0.9rem;
    }
    
    .project-tech {
      .tech-tags {
        gap: 0.375rem;
      }
      
      .tech-tag {
        font-size: 0.75rem;
        padding: 0.15rem 0.5rem;
      }
    }
    
    .project-outcomes li {
      font-size: 0.85rem;
      padding-left: 1.25rem;
    }
  }
`;

const CaseStudySection = styled.div`
  text-align: center;
  padding: 4rem 0;
  margin: 4rem 0;
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);
  border-radius: 16px;

  h2 {
    font-size: 2.5rem;
    font-weight: 400;
    margin-bottom: 2rem;
    color: var(--accent-primary);
  }

  p {
    font-size: 1.125rem;
    line-height: 1.6;
    color: var(--text-secondary);
    max-width: 700px;
    margin: 0 auto 2rem;
    padding: 0 1rem;
  }

  .cta-button {
    display: inline-block;
    background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
    color: white;
    padding: 1rem 2rem;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(157, 78, 221, 0.3);
    }
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
    margin: 3rem 0;
    
    h2 {
      font-size: 2rem;
    }
    
    p {
      font-size: 1rem;
      padding: 0 0.5rem;
    }
    
    .cta-button {
      padding: 0.875rem 1.75rem;
      font-size: 0.95rem;
    }
  }

  @media (max-width: 480px) {
    padding: 2rem 0.75rem;
    
    h2 {
      font-size: 1.75rem;
    }
    
    p {
      font-size: 0.95rem;
      padding: 0 0.25rem;
    }
    
    .cta-button {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
    }
  }
`;

function OurWork() {
  const projects = [
    {
      title: "Mechanic AI",
      type: "AI-Powered Mobile Application",
      description: "Revolutionary iOS app that leverages artificial intelligence to help users diagnose and troubleshoot car issues. Users can describe symptoms or upload photos/videos of car problems to receive instant AI-powered diagnostic recommendations and repair guidance.",
      technologies: ["Swift", "SwiftUI", "CoreML", "Vision Framework", "OpenAI API", "Firebase", "CloudKit"],
      outcomes: [
        "Instant AI-powered car diagnostics",
        "Photo/video analysis for visual problems",
        "Step-by-step repair guidance",
        "Cost estimation for common repairs",
        "Integration with local mechanic finder"
      ],
      delay: "0s"
    },
    {
      title: "Effect-01",
      type: "Creative Video Processing App",
      description: "Innovative iOS application for creating stunning video glitch effects and digital art. Features real-time video processing with customizable glitch parameters, allowing users to transform ordinary videos into captivating visual experiences with professional-grade effects.",
      technologies: ["Swift", "Metal", "AVFoundation", "Core Image", "Core Animation", "GPUImage", "FFmpeg"],
      outcomes: [
        "Real-time video glitch processing",
        "20+ customizable effect parameters",
        "Export in multiple resolutions up to 4K",
        "Intuitive gesture-based controls",
        "Social media integration for sharing"
      ],
      delay: "1s"
    },
    {
      title: "Crypto Chat",
      type: "Financial Social Platform",
      description: "Comprehensive iOS app combining real-time cryptocurrency market data with social networking features. Users can discuss market trends, share trading insights, and access live price feeds while connecting with a community of crypto enthusiasts and traders.",
      technologies: ["Swift", "SwiftUI", "WebSocket", "CoinGecko API", "Firebase Realtime DB", "Push Notifications", "Charts Framework"],
      outcomes: [
        "Real-time crypto price tracking for 1000+ coins",
        "Live chat with market sentiment analysis",
        "Portfolio tracking and performance metrics",
        "Price alerts and market notifications",
        "Community-driven trading insights"
      ],
      delay: "2s"
    },
    {
      title: "Enterprise AI Agent Bots",
      type: "AI Automation & Integration",
      description: "Custom AI agent bots designed to automate business processes and enhance customer interactions. These intelligent agents handle everything from customer support and lead qualification to internal workflow automation, integrating seamlessly with existing business systems.",
      technologies: ["OpenAI API", "LangChain", "Python", "FastAPI", "PostgreSQL", "Redis", "Slack API", "Zapier Integration"],
      outcomes: [
        "80% reduction in customer response time",
        "24/7 automated customer support coverage",
        "Lead qualification automation with 90% accuracy",
        "Integration with CRM and business tools",
        "Custom knowledge base training and deployment"
      ],
      delay: "0.5s"
    }
  ];

  return (
    <>
      <SEOHelmet 
        title="Our Work - Big Brain iOS App Development"
        description="Explore our portfolio including iOS apps (Mechanic AI, Effect-01, Crypto Chat) and enterprise AI agent bots for business automation and customer support."
        canonicalUrl="https://bigbrainthings.xyz/our-work"
      />
      
      <OurWorkContainer>
        <MainContent>
          <HeroSection>
            <h1>Our Work</h1>
            <p>
              Explore our portfolio of innovative solutions that showcase our expertise 
              in mobile development, AI integration, and automation. From AI-powered iOS apps 
              to intelligent business automation bots, we build technology that solves real problems 
              and delivers exceptional user experiences.
            </p>
          </HeroSection>

          <ProjectsGrid>
            {projects.map((project, index) => (
              <ProjectCard key={index} delay={project.delay}>
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <div className="project-type">{project.type}</div>
                </div>
                
                <div className="project-description">
                  <p>{project.description}</p>
                </div>
                
                <div className="project-tech">
                  <h4>Technologies Used</h4>
                  <div className="tech-tags">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
                
                <div className="project-outcomes">
                  <h4>Key Outcomes</h4>
                  <ul>
                    {project.outcomes.map((outcome, outcomeIndex) => (
                      <li key={outcomeIndex}>{outcome}</li>
                    ))}
                  </ul>
                </div>
              </ProjectCard>
            ))}
          </ProjectsGrid>

          <CaseStudySection>
            <h2>Ready to Build Your Next App?</h2>
            <p>
              These projects demonstrate our ability to create innovative mobile solutions 
              across diverse industries. Whether you need AI integration, real-time data 
              processing, or creative multimedia features, we have the expertise to bring 
              your vision to life.
            </p>
            <a href="mailto:bigbrainthingsandsuch@gmail.com" className="cta-button">
              Start Your Project
            </a>
          </CaseStudySection>
        </MainContent>
      </OurWorkContainer>
    </>
  );
}

export default OurWork;
